import { suggestCategory } from '@fd/finance-core';
import { parseMoneyInput } from '@fd/money';
import { newId, type Transaction } from '@fd/schemas';
import { ValidationError } from '@fd/sync-engine';
import { Check } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { categoryExpenseType, chooseable, today, useAccounts, useCategories, useCategoryGroups, useCategoryMemory, useHousehold, usePersons } from '../app/data.ts';
import { useT } from '../app/i18n.tsx';
import { useSession } from '../app/session.tsx';
import { AmountInput } from '../ui/AmountInput.tsx';
import { DateInput } from '../ui/DateInput.tsx';
import { Avatar, Modal, Segmented, useToast } from '../ui/primitives.tsx';
import { StableHeight } from '../ui/StableHeight.tsx';
import { CategoryOptions } from '../ui/CategoryOptions.tsx';

export type QuickAddKind = 'EXPENSE' | 'INCOME' | 'TRANSFER';
type Kind = QuickAddKind;

/** Quick add (spec §61): a manual expense in a few seconds, with payer and who it is for. */
export function QuickAdd({ open, initialKind, onClose }: { open: boolean; initialKind?: Kind; onClose(): void }) {
  const t = useT();
  const { engine, householdId, requestSync, status } = useSession();
  const household = useHousehold();
  const accounts = useAccounts();
  const categories = useCategories();
  const groups = useCategoryGroups();
  const persons = usePersons();
  const toast = useToast();

  const [kind, setKind] = useState<Kind>('EXPENSE');
  const [amount, setAmount] = useState('');
  const [merchant, setMerchant] = useState('');
  const [date, setDate] = useState(today());
  const [accountId, setAccountId] = useState('');
  const [toAccountId, setToAccountId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [categoryPicked, setCategoryPicked] = useState(false);
  const { memory } = useCategoryMemory();
  const [shared, setShared] = useState(true);
  const [paidBy, setPaidBy] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const account = accounts.find((a) => a.id === accountId);
  const currency = account?.currency ?? household?.baseCurrency ?? 'GBP';

  useEffect(() => {
    if (!open) return;
    if (initialKind) setKind(initialKind);
    setAmount('');
    setMerchant('');
    setCategoryPicked(false);
    setDate(today());
    setError(null);
    setSaving(false);
    const first = accounts.find((a) => a.type === 'CURRENT' || a.type === 'CREDIT_CARD') ?? accounts[0];
    setAccountId(first?.id ?? '');
    setToAccountId(accounts.find((a) => a.id !== first?.id)?.id ?? '');
    setPaidBy(household?.ownerPersonId ?? persons[0]?.id ?? '');
  }, [open]); // reset only when the dialog opens

  const expenseCategories = useMemo(() => chooseable(categories).filter((c) => c.kind === (kind === 'INCOME' ? 'INCOME' : 'EXPENSE')), [categories, kind]);

  async function save() {
    if (!householdId) return;
    const parsed = parseMoneyInput(amount, currency, t.locale);
    if (!parsed.ok || parsed.minor === 0n) {
      setError(parsed.ok || parsed.error !== 'TOO_MANY_DECIMALS' ? t.t('quickAdd.amountInvalid') : t.t('quickAdd.amountTooManyDecimals', { currency }));
      return;
    }
    if (!accountId) {
      setError(t.t('quickAdd.required'));
      return;
    }
    const magnitude = parsed.minor < 0n ? -parsed.minor : parsed.minor;
    const base = {
      bookingDate: date,
      valueDate: null,
      currency,
      description: null,
      status: 'BOOKED',
      source: 'MANUAL',
      splitMethod: null,
      participants: [],
      refundOfTransactionId: null,
      splits: [],
      tagIds: [],
      notes: null,
      needsReview: false,
    } satisfies Partial<Transaction>;
    setSaving(true);
    try {
      if (kind === 'TRANSFER') {
        if (!toAccountId || toAccountId === accountId) throw new Error(t.t('quickAdd.required'));
        const target = accounts.find((a) => a.id === toAccountId);
        const label = merchant.trim() || `${account?.name ?? ''} → ${target?.name ?? ''}`;
        await engine.create(householdId, 'transaction', newId(), {
          ...base,
          accountId,
          amountMinor: String(-magnitude),
          merchantName: label,
          categoryId: null,
          kind: 'TRANSFER',
          expenseType: 'EXCLUDED',
          paidByPersonId: null,
          counterpartAccountId: toAccountId,
        });
        if (target && target.currency === currency) {
          await engine.create(householdId, 'transaction', newId(), {
            ...base,
            accountId: toAccountId,
            amountMinor: String(magnitude),
            merchantName: label,
            categoryId: null,
            kind: 'TRANSFER',
            expenseType: 'EXCLUDED',
            paidByPersonId: null,
            counterpartAccountId: accountId,
          });
        }
      } else {
        await engine.create(householdId, 'transaction', newId(), {
          ...base,
          accountId,
          amountMinor: String(kind === 'EXPENSE' ? -magnitude : magnitude),
          merchantName: merchant.trim() || null,
          categoryId: categoryId || null,
          kind,
          expenseType: kind === 'EXPENSE' ? (shared ? 'SHARED' : 'PERSONAL') : null,
          paidByPersonId: kind === 'EXPENSE' && paidBy ? paidBy : null,
          counterpartAccountId: null,
        });
      }
      requestSync();
      toast(status.online ? t.t('quickAdd.saved') : t.t('quickAdd.savedOffline'), <Check aria-hidden="true" />);
      onClose();
    } catch (cause) {
      setSaving(false);
      setError(cause instanceof ValidationError ? t.t('errors.validation') : cause instanceof Error ? cause.message : String(cause));
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t.t('quickAdd.title')}
      footer={
        <>
          <button className="btn btn--ghost" type="button" onClick={onClose}>
            {t.t('common.cancel')}
          </button>
          <button className="btn btn--primary" type="button" onClick={() => void save()} disabled={saving}>
            {t.t('common.save')}
          </button>
        </>
      }
    >
      {/* An expense has more fields than income or a transfer: the dialog keeps its size across them. */}
      <StableHeight>
        <form
          className="stack gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <Segmented<Kind>
            label={t.t('quickAdd.title')}
            value={kind}
            onChange={setKind}
            options={[
              { value: 'EXPENSE', label: t.t('quickAdd.expense') },
              { value: 'INCOME', label: t.t('quickAdd.income') },
              { value: 'TRANSFER', label: t.t('quickAdd.transfer') },
            ]}
          />
          <label className="field">
            <span className="field__label">
              {t.t('transactions.amount')} ({currency})
            </span>
            <AmountInput
              className="input amount-input"
              currency={currency}
              placeholder="0,00"
              value={amount}
              onValue={(value) => {
                setAmount(value);
                setError(null);
              }}
              aria-invalid={error !== null}
              autoFocus
            />
            {error && (
              <span className="field__error" role="alert">
                {error}
              </span>
            )}
          </label>
          <div className="form-grid">
            <label className="field">
              <span className="field__label">{kind === 'TRANSFER' ? t.t('quickAdd.from') : t.t('transactions.account')}</span>
              <select className="select" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} · {a.currency}
                  </option>
                ))}
              </select>
            </label>
            {kind === 'TRANSFER' ? (
              <label className="field">
                <span className="field__label">{t.t('quickAdd.to')}</span>
                <select className="select" value={toAccountId} onChange={(e) => setToAccountId(e.target.value)}>
                  {accounts
                    .filter((a) => a.id !== accountId)
                    .map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name} · {a.currency}
                      </option>
                    ))}
                </select>
              </label>
            ) : (
              <label className="field">
                <span className="field__label">{t.t('transactions.date')}</span>
                <DateInput value={date} onChange={(e) => setDate(e.target.value)} />
              </label>
            )}
            <label className="field">
              <span className="field__label">{t.t('transactions.merchant')}</span>
              <input
                className="input"
                value={merchant}
                onChange={(e) => {
                  setMerchant(e.target.value);
                  // The category the household usually gives this merchant, until one is picked by hand.
                  if (!categoryPicked && kind !== 'TRANSFER') {
                    const hint = suggestCategory(memory, { id: '', merchant: e.target.value, description: null, kind: kind === 'INCOME' ? 'INCOME' : 'EXPENSE', categoryId: null });
                    setCategoryId(hint?.categoryId ?? '');
                    const preset = categoryExpenseType(categories, hint?.categoryId, today());
                    if (preset) setShared(preset === 'SHARED');
                  }
                }}
                placeholder={kind === 'EXPENSE' ? 'Tesco' : ''}
              />
            </label>
            {kind !== 'TRANSFER' ? (
              <label className="field">
                <span className="field__label">{t.t('transactions.category')}</span>
                <select
                  className="select"
                  value={categoryId}
                  onChange={(e) => {
                    setCategoryId(e.target.value);
                    setCategoryPicked(true);
                    // A category shared by default (D-037) sets the switch below; it can still be changed.
                    const preset = categoryExpenseType(categories, e.target.value, today());
                    if (preset) setShared(preset === 'SHARED');
                  }}
                >
                  <option value="">{t.t('transactions.uncategorised')}</option>
                  <CategoryOptions categories={expenseCategories} groups={groups} />
                </select>
              </label>
            ) : (
              <label className="field">
                <span className="field__label">{t.t('transactions.date')}</span>
                <DateInput value={date} onChange={(e) => setDate(e.target.value)} />
              </label>
            )}
          </div>
          {kind === 'EXPENSE' && (
            <>
              <div className="stack gap-2">
                <span className="field__label">{t.t('transactions.paidBy')}</span>
                <div className="chip-group">
                  {persons.map((p) => (
                    <button key={p.id} type="button" className="chip" aria-pressed={paidBy === p.id} onClick={() => setPaidBy(p.id)}>
                      <Avatar name={p.displayName} color={p.color} />
                      {p.displayName}
                    </button>
                  ))}
                </div>
              </div>
              <div className="stack gap-2">
                <span className="field__label">{t.t('transactions.expenseType')}</span>
                <Segmented
                  label={t.t('transactions.expenseType')}
                  value={shared ? 'SHARED' : 'PERSONAL'}
                  onChange={(v) => setShared(v === 'SHARED')}
                  options={[
                    { value: 'SHARED', label: t.t('expenseType.SHARED') },
                    { value: 'PERSONAL', label: t.t('expenseType.PERSONAL') },
                  ]}
                />
              </div>
            </>
          )}
          <button type="submit" hidden />
        </form>
      </StableHeight>
    </Modal>
  );
}
